import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Save, Loader2, Globe } from "lucide-react";
import api from "@/lib/api";

export default function Prices() {
  const { t } = useTranslation();
  const [prices, setPrices] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [refreshingKey, setRefreshingKey] = useState(null);
  const [saved, setSaved] = useState(null);
  const [refreshed, setRefreshed] = useState(null);

  const load = async () => {
    const { data } = await api.get("/prices");
    setPrices(data);
  };
  useEffect(() => { load(); }, []);

  const setField = (key, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const save = async (p) => {
    setSavingKey(p.key);
    try {
      const merged = { ...p, ...edits[p.key] };
      merged.material = parseFloat(merged.material);
      merged.labor = parseFloat(merged.labor);
      await api.put(`/prices/${p.key}`, {
        key: merged.key, label: merged.label, unit: merged.unit,
        material: merged.material, labor: merged.labor, category: merged.category,
      });
      await load();
      setEdits((prev) => { const n = { ...prev }; delete n[p.key]; return n; });
      setSaved(p.key);
      setTimeout(() => setSaved(null), 1600);
    } finally {
      setSavingKey(null);
    }
  };

  const refreshFromInternet = async (p) => {
    setRefreshingKey(p.key);
    try {
      const r = await api.post(`/pricing/refresh/${p.key}`);
      if (r.data?.ok) {
        await load();
        setRefreshed(p.key);
        setTimeout(() => setRefreshed(null), 2400);
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingKey(null);
    }
  };

  const byCat = {};
  for (const p of prices) {
    (byCat[p.category] ||= []).push(p);
  }

  return (
    <div className="p-6" data-testid="prices-page">
      <header className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">IQES · IQ-APP</div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">{t("prices.title")}</h1>
        <p className="text-sm text-slate-600 mt-1">{t("prices.subtitle")}</p>
      </header>

      {Object.entries(byCat).map(([cat, rows]) => (
        <section key={cat} className="mb-6">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">{cat.replace("_", " ")}</h2>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full" data-testid={`prices-${cat}`}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="text-left p-3">Key</th>
                  <th className="text-left p-3">Label</th>
                  <th className="text-left p-3 w-20">Unit</th>
                  <th className="text-left p-3 w-32">Material $</th>
                  <th className="text-left p-3 w-32">Labor $</th>
                  <th className="p-3 w-28" />
                  <th className="p-3 w-28" />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const e = edits[p.key] || {};
                  const dirty = Object.keys(e).length > 0;
                  return (
                    <tr key={p.key} data-testid={`price-row-${p.key}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-xs font-mono text-slate-500">{p.key}</td>
                      <td className="p-3 text-sm">
                        <input
                          data-testid={`price-label-${p.key}`}
                          value={e.label ?? p.label}
                          onChange={(ev) => setField(p.key, "label", ev.target.value)}
                          className="w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-xl px-2 py-1 focus:outline-none"
                        />
                      </td>
                      <td className="p-3 text-xs font-mono text-slate-700">{p.unit}</td>
                      <td className="p-3 font-mono">
                        <input
                          data-testid={`price-material-${p.key}`}
                          type="number"
                          step="0.01"
                          value={e.material ?? p.material}
                          onChange={(ev) => setField(p.key, "material", ev.target.value)}
                          className="w-24 bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-xl px-2 py-1 focus:outline-none text-sm"
                        />
                      </td>
                      <td className="p-3 font-mono">
                        <input
                          data-testid={`price-labor-${p.key}`}
                          type="number"
                          step="0.01"
                          value={e.labor ?? p.labor}
                          onChange={(ev) => setField(p.key, "labor", ev.target.value)}
                          className="w-24 bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-xl px-2 py-1 focus:outline-none text-sm"
                        />
                      </td>
                      <td className="p-3 text-right">
                        <button
                          data-testid={`save-price-${p.key}`}
                          onClick={() => save(p)}
                          disabled={!dirty || savingKey === p.key}
                          className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl inline-flex items-center gap-1.5 transition-colors ${
                            dirty
                              ? "bg-[#003366] text-white hover:bg-[#002244]"
                              : "bg-slate-100 text-slate-400 cursor-not-allowed"
                          }`}
                        >
                          {savingKey === p.key ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          {saved === p.key ? t("prices.saved") : t("prices.save")}
                        </button>
                      </td>
                      <td className="p-3 text-right">
                        <button
                          data-testid={`refresh-price-${p.key}`}
                          onClick={() => refreshFromInternet(p)}
                          disabled={refreshingKey === p.key}
                          title="Buscar precio actual en internet (IA + BLS)"
                          className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl inline-flex items-center gap-1.5 bg-[#EA580C]/10 text-[#EA580C] hover:bg-[#EA580C]/20 disabled:opacity-50"
                        >
                          {refreshingKey === p.key ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />}
                          {refreshed === p.key ? "Actualizado" : "Internet"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
